import {type AutocompleteState, createAutocomplete} from '@algolia/autocomplete-core';
import {getAlgoliaResults} from '@algolia/autocomplete-preset-algolia';
import type {FacetFilters, Hit} from "algoliasearch";
import type {AlgoliaProductAttributes} from "../../AlgoliaProductAttributes";
import {
    type BaseSyntheticEvent,
    createContext,
    type Dispatch,
    type FC,
    type  KeyboardEvent,
    type  MouseEvent,
    type ReactNode,
    type RefObject,
    type SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useConfigContext} from "@/ConfigProvider";
import {initializeHeaderSearchIntegration} from '../utils/headerSearchIntegration';
import {AnalyticsProvider} from "@/providers/AnalyticsProvider";
import {debouncePromise} from "../../utils/debounce";
import {MIN_QUERY_LENGTH, SEARCH_DEBOUNCE_MS} from "../../utils/searchConfig";

type AutocompleteCoreState = AutocompleteState<Hit<AlgoliaProductAttributes>>;

interface AutocompleteContextProps {
    autocompleteState: AutocompleteCoreState;
    setAutocompleteState: Dispatch<SetStateAction<AutocompleteCoreState>>;
    setFilters: Dispatch<SetStateAction<string>>;
    setFacetFilters: Dispatch<SetStateAction<FacetFilters>>;
    autocomplete: ReturnType<typeof createAutocomplete<Hit<AlgoliaProductAttributes>, BaseSyntheticEvent, MouseEvent, KeyboardEvent>>;
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    forceModalOpen: boolean;
    setForceModalOpen: Dispatch<SetStateAction<boolean>>;
    inputRef: RefObject<HTMLInputElement | null>
    formRef: RefObject<HTMLFormElement | null>
    panelRef: RefObject<HTMLDivElement | null>
    searchRef: RefObject<HTMLDivElement | null>
    totalHits?: number
}

const AutocompleteContext = createContext<AutocompleteContextProps | undefined>(undefined);

export const AutocompleteProvider: FC<{ children: ReactNode }> = ({children}) => {
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [filters, setFilters] = useState<string>("");
    const [facetFilters, setFacetFilters] = useState<FacetFilters>("");
    const [totalHits] = useState<number>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [forceModalOpen, setForceModalOpen] = useState(false);

    const {
        tc,
        searchClient,
        algoliaConfig: {algoliaIndexName, customizedUrl, autocompleteProductHits, algoliaShopFilter}
    } = useConfigContext();

    const [autocompleteState, setAutocompleteState] = useState<AutocompleteCoreState>({
        query: '',
        collections: [],
        isOpen: false,
        activeItemId: null,
        completion: null,
        context: {},
        status: 'idle'
    });

    const filtersRef = useRef(filters);
    const facetFiltersRef = useRef(facetFilters);
    useEffect(() => {
        filtersRef.current = filters;
        autocomplete.refresh().then();
    }, [filters]);

    useEffect(() => {
        facetFiltersRef.current = facetFilters;
        autocomplete.refresh().then();
    }, [facetFilters]);
    const autocomplete = useMemo(
        () => {
            const debouncedGetSources = debouncePromise((query: string) => [
                {
                    sourceId: 'products',
                    getItems() {
                        return getAlgoliaResults<Hit<AlgoliaProductAttributes>>({
                            searchClient,
                            queries: [
                                {
                                    indexName: algoliaIndexName,
                                    params: {
                                        query: query || '*', // Use wildcard search when no query
                                        filters: [algoliaShopFilter, filtersRef.current].filter(Boolean).join(' AND '),
                                        facetFilters: facetFiltersRef.current,
                                        hitsPerPage: parseInt(autocompleteProductHits, 10),
                                    },
                                },
                            ]
                        })
                    },
                },
                {
                    sourceId: 'categories',
                    getItems() {
                        if (!query) return [];
                        // Uses the same searchClient as the 'products' source above, so
                        // autocomplete-core batches both into a single Algolia request
                        // instead of firing two separate network calls per search.
                        return getAlgoliaResults<Hit<AlgoliaProductAttributes>>({
                            searchClient,
                            queries: [
                                {
                                    indexName: algoliaIndexName,
                                    params: {
                                        query,
                                        hitsPerPage: 8,
                                        filters: algoliaShopFilter ?? '',
                                        typoTolerance: true,
                                    },
                                },
                            ]
                        })
                    },
                },
            ], SEARCH_DEBOUNCE_MS);

            return createAutocomplete<Hit<AlgoliaProductAttributes>, BaseSyntheticEvent, MouseEvent, KeyboardEvent>({
                onSubmit({state}) {
                    window.location.href = `/${customizedUrl}/search/?input_search=${state.query}`;
                },
                onStateChange({state}) {
                    setAutocompleteState(state);
                },
                insights: true,
                placeholder: tc.placeholder,
                openOnFocus: true,
                getSources({query}) {
                    // Show sources even without query when modal is force opened
                    if (!query && !forceModalOpen) return [];
                    // Only search once the user has typed a meaningful query
                    if (query && query.length < MIN_QUERY_LENGTH) return [];
                    return debouncedGetSources(query);
                },
            });
        },
        [forceModalOpen, tc.placeholder, searchClient, algoliaIndexName, customizedUrl, autocompleteProductHits, algoliaShopFilter]
    );
    const {getEnvironmentProps} = autocomplete;

    useEffect(() => {
        if (!formRef.current || !panelRef.current || !inputRef.current) {
            return undefined;
        }
        const {onTouchStart, onTouchMove, onMouseDown} = getEnvironmentProps({
            formElement: formRef.current,
            inputElement: inputRef.current,
            panelElement: panelRef.current,
        });
        window.addEventListener('mousedown', onMouseDown);
        window.addEventListener('touchstart', onTouchStart);
        window.addEventListener('touchmove', onTouchMove);
        return () => {
            window.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
        };
    }, [getEnvironmentProps, autocompleteState.isOpen]);

    // Initialize header search integration
    useEffect(() => {
        initializeHeaderSearchIntegration();
    }, []);

    // Listen for custom event from headerSearch--minimal button
    useEffect(() => {
        const handleToggleModal = (_event: CustomEvent) => {
            if (isModalOpen && forceModalOpen) {
                // Modal is open via minimal search, close it
                setForceModalOpen(false);
                setIsModalOpen(false);
            } else {
                // Modal is closed, open it
                setForceModalOpen(true);
                setIsModalOpen(true);
                // Focus the input after a short delay to ensure modal is open
                setTimeout(() => {
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                }, 100);
            }
        };

        document.addEventListener('openAutocompleteModal', handleToggleModal as EventListener);

        return () => {
            document.removeEventListener('openAutocompleteModal', handleToggleModal as EventListener);
        };
    }, [isModalOpen, forceModalOpen, setIsModalOpen, setForceModalOpen, inputRef]);

    return (
        <AnalyticsProvider>
        <AutocompleteContext.Provider value={{
            searchRef,
            panelRef,
            formRef,
            inputRef,
            autocompleteState,
            setAutocompleteState,
            autocomplete,
            setFilters,
            setFacetFilters,
            setIsModalOpen,
            isModalOpen,
            forceModalOpen,
            setForceModalOpen,
            totalHits,

        }}>
            {children}
        </AutocompleteContext.Provider>
        </AnalyticsProvider>
    );
};

export const useAutocomplete = () => {
    const context = useContext(AutocompleteContext);
    if (!context) {
        throw new Error('useAutocomplete must be used within an AutocompleteProvider');
    }
    return context;
};

